// Applies the device's theme to the whole page.
//
// Promise: `data-theme` on <html> is "light" or "dark" ("auto" follows the OS
// and its changes), so every color token switches everywhere, portals and
// layers included. `color-scheme` and the browser's theme-color follow.
import { useEffect, useSyncExternalStore } from "react";
import { useDeviceSetting } from "./device-setting";
import { themeSetting } from "./settings";

/** The color touching the screen's edges (the panel surface), for browser bars. */
const EDGE = { light: "#ffffff", dark: "#1b1b20" } as const;

const dark = "(prefers-color-scheme: dark)";
const subscribeSystem = (onChange: () => void) => {
  const q = window.matchMedia(dark);
  q.addEventListener("change", onChange);
  return () => q.removeEventListener("change", onChange);
};
const systemDark = () => window.matchMedia(dark).matches;

export function useIsDark(): boolean {
  const [theme] = useDeviceSetting(themeSetting);
  const system = useSyncExternalStore(subscribeSystem, systemDark, () => false);
  return theme === "dark" || (theme === "auto" && system);
}

export function applyTheme(isDark: boolean): void {
  const root = document.documentElement;
  root.dataset.theme = isDark ? "dark" : "light";
  // Safari repaints its bars only for a new element, not a changed content.
  document.head.querySelector('meta[name="theme-color"]')?.remove();
  const meta = document.createElement("meta");
  meta.name = "theme-color";
  meta.content = isDark ? EDGE.dark : EDGE.light;
  document.head.appendChild(meta);
}

/**
 * Paints the page for the stored theme at once, before anything renders: a
 * dark app first drew its page white until the app's effect ran.
 */
export function applyStoredTheme(): void {
  const theme = themeSetting.read();
  applyTheme(theme === "dark" || (theme === "auto" && systemDark()));
}

/** Keeps <html data-theme> in step with the device setting (mounted once). */
export function useApplyTheme(): void {
  const isDark = useIsDark();
  // frontend-development: not a fetch; it paints the page for the chosen theme.
  useEffect(() => applyTheme(isDark), [isDark]);
}
